@if (dataSource) {
  <div class="example-container position-relative mat-elevation-z8">
    @if (loadingContent) {
      <div class="spinner-overlay position-absolute d-flex align-items-center justify-content-center">
        <div class="spinner-border text-primary" role="status">
          <span class="visually-hidden">Loading...</span>
        </div>
      </div>
    }
    @if (!loadingContent) {
      <table
        mat-table
        [dataSource]="dataSource"
        multiTemplateDataRows
        [trackBy]="trackByRow"
        [attr.aria-label]="'DRYER_FILTER_TABLE_DESCRIPTION' | translate">
        <!-- Checkbox Column -->
        <ng-container matColumnDef="select">
          <th mat-header-cell *matHeaderCellDef class="border-right border-white fw-bold text-center">{{ 'SELECT' | translate }}</th>
          <td mat-cell *matCellDef="let row" class="border-right border-white">
            <mat-checkbox
              color="warn"
              (click)="$event.stopPropagation()"
              (change)="onCheckboxChange($event, row)"
              [checked]="isSelected(row)">
            </mat-checkbox>
          </td>
        </ng-container>
        <!-- Dynamic Columns -->
        @for (column of displayColumns; track column) {
          <ng-container [matColumnDef]="column">
            <th mat-header-cell *matHeaderCellDef class="border-right border-white fw-bold text-center">
              {{ columnLabels[column] | translate }}
              @if (columnToUnitMap[column] && units[columnToUnitMap[column]]) {
                <span class="unit-text"> ({{ units[columnToUnitMap[column]] }}) </span>
              }
            </th>
            <td
              mat-cell
              *matCellDef="let element"
              class="border-right border-white fw-normal text-center"
              [ngClass]="{ 'single-line-header': column === 'model' }">
              <span
                [ngClass]="{
                  'text-dangertext-decoration-underline fw-bold':
                    column === 'percentage' && tab === 'model' && isPercentageNegative(element),
                }">
                <span>
                  {{
                    column === 'dryerType' || column === 'dryerCategory' || column === 'dryerGroup'
                      ? (getTranslationKey(element, column) | translate)
                      : getColumnValue(element, column)
                  }}
                </span>
              </span>
              @if (column === 'model' && element.warningMessage && (element.model || element.exactModel?.model)) {
                <div>
                  @if (
                    (element.warningMessage && element.warningMessage !== '') ||
                    (element.suggestedModels && element.suggestedModels.warningMessage && element.suggestedModels.warningMessage !== '')
                  ) {
                    <span
                      class="warning-icon text-warning"
                      data-bs-toggle="tooltip"
                      [attr.title]="element.warningMessage ? element.warningMessage : element.suggestedModels.warningMessage">
                      ⚠️
                    </span>
                  }
                </div>
              }
            </td>
          </ng-container>
        }
        <!-- Download PDF Column -->
        <ng-container matColumnDef="download">
          <th mat-header-cell *matHeaderCellDef class="fw-bold text-center">{{ 'DOWNLOAD_PDF' | translate }}</th>
          <td mat-cell *matCellDef="let row" class="border-right border-white">
            <button
              type="button"
              class="download-btn"
              (click)="downloadPdf($event, row?.exactModel?.model ?? row?.model, row?.isHeaterRequired)">
              <img src="../../../../assets/img/download.svg" />
            </button>
          </td>
        </ng-container>
        <!-- Header and Row Definitions -->
        <tr mat-header-row *matHeaderRowDef="displayedColumns" class="bg-info border-bottom border-white"></tr>
        <tr
          mat-row
          *matRowDef="let row; columns: displayedColumns"
          class="element-row border-bottom w-75"
          [ngClass]="getRowClasses(row)"
          (click)="toggleRowDetail(row)"></tr>
        <!-- Expanded Detail Row -->
        <ng-container matColumnDef="expandedDetail">
          <td mat-cell *matCellDef="let row" [attr.colspan]="displayedColumns.length">
            @if (row.expanded) {
              <div class="model-details d-flex align-items-center p-2 bg-white">
                <div class="model-img d-flex flex-column justify-content-center text-center">
                  <!-- Show spinner if image is loading -->
                  @if (row.isImageLoading) {
                    <div class="spinner-border text-primary" role="status">
                      <span class="visually-hidden">Loading...</span>
                    </div>
                  }
                  @if (row.imageSource && !row.isImageLoading) {
                    <img class="model-image" [src]="row.imageSource" alt="" />
                  }
                  <div>
                    <b class="model-text fw-normal">{{ 'MODEL' | translate }}: </b>
                    <span class="model-value-text text-primary fw-normal">{{ row?.exactModel?.model || row?.model }}</span>
                  </div>
                </div>
                <div class="grid-container px-1 pb-4 d-flex justify-content-start gap-2 flex-wrap align-items-center w-100">
                  <div class="grid-item p-3">
                    <span class="fw-normal">{{ 'FIELDS.PRESSURE_DROP' | translate }}:</span>
                    <span class="fw-light">{{ hasValidPressureDrop(row) ? (row.pressureDrop | number: '1.2-2') : '-' }}</span>
                    @if (hasValidPressureDrop(row)) {
                      <span class="fw-light"> bar</span>
                    }
                  </div>
                  @if (row.purgeFlow) {
                    <div class="grid-item p-3">
                      <span class="fw-normal">{{ 'PURGE_AIR_FLOW' | translate }}:</span>
                      <span class="fw-light">{{ row.purgeFlow !== null && row.purgeFlow !== 0 ? row.purgeFlow : '-' }}</span>
                      @if (row.purgeFlow !== null && row.purgeFlow !== 0) {
                        <span class="fw-light">m³/h</span>
                      }
                    </div>
                  }
                  @if (row.outletAirFlow) {
                    <div class="grid-item p-3">
                      <span class="fw-normal">{{ 'OUTLET_AIR_FLOW' | translate }}:</span>
                      <span class="fw-light">{{ row.outletAirFlow !== null && row.outletAirFlow !== 0.0 ? row.outletAirFlow : '-' }}</span>
                      @if (row.outletAirFlow !== null && row.outletAirFlow !== 0.0) {
                        <span class="fw-light">m³/h</span>
                      }
                    </div>
                  }
                  @if (powerSupply) {
                    <div class="grid-item p-3">
                      <span class="fw-normal">{{ 'FIELDS.POWER_SUPPLY' | translate }}:</span>
                      <span class="fw-light">{{ powerSupply || '-' }}</span>
                      @if (powerSupply !== null) {
                        <span class="fw-light">(V / f / Hz)</span>
                      }
                    </div>
                  }
                  @for (entry of getFilteredEntries(row); track entry.key) {
                    <div class="grid-item p-3">
                      <span class="fw-normal">{{ getTranslatedFieldLabel(entry.key) | translate }}: </span>
                      <span class="fw-light">{{ entry.value }}</span>
                      @if (entry.value !== '-') {
                        <span class="fw-light">{{ getDetailUnit(entry.key) }}</span>
                      }
                    </div>
                  }
                </div>
              </div>
            }
          </td>
        </ng-container>
        <tr mat-row *matRowDef="let row; columns: ['expandedDetail']" class="expanded-detail-row" [class.display-none]="!row.expanded"></tr>
      </table>
      @if (dataSource.data && dataSource?.data?.length === 0 && showNoModelsMessage) {
        <div>
          <!-- Case 1: All dryers are within range but no models are available -->
          @if (hasAllInRangeButNoModels()) {
            <div class="no-data-message text-center mt-4">
              <h5>{{ 'ERROR_MESSAGES.NO_MODELS_AVAILABLE_MESSAGE' | translate }}</h5>
              <p>{{ 'NO_MODELS_CONTACT_SALES' | translate }}</p>
            </div>
          }
          <!-- Case 2: Some dryers are in range but no models are available, others are out of range -->
          @if (hasSomeInRangeButNoModels() && hasSomeOutOfRange()) {
            <div class="no-data-message mt-4">
              <h5 class="text-center mb-3">
                {{ 'ERROR_MESSAGES.SOME_DRYER_TYPES_IN_RANGE_MESSAGE' | translate }}
              </h5>
              <ng-container *ngTemplateOutlet="dryerRangesAccordion"></ng-container>
            </div>
          }
          <!-- Case 3: All dryers are out of range -->
          @if (!hasAnyItemsWithinRange()) {
            <div class="no-data-message mt-4">
              <h5 class="text-center mb-3">{{ 'ERROR_MESSAGES.NO_DRYERS_AVAILABLE_MESSAGE' | translate }}</h5>
              <ng-container *ngTemplateOutlet="dryerRangesAccordion"></ng-container>
            </div>
          }
        </div>
      }
      <!-- Template for dryer ranges accordion -->
      <ng-template #dryerRangesAccordion>
        <div class="range-info-container">
          <div class="accordion text-start" id="dryerSpecsAccordion">
            <!-- Dynamic accordion items -->
            @for (config of visibleAccordions; track trackByAccordionId($index, config)) {
              <div class="accordion-item">
                <!-- Accordion Header -->
                <h2 class="accordion-header" [id]="config.id + 'Heading'">
                  <button
                    class="accordion-button text-start"
                    [class.collapsed]="!isAccordionExpanded(config.id)"
                    type="button"
                    data-bs-toggle="collapse"
                    [attr.data-bs-target]="'#' + config.id + 'Collapse'"
                    [attr.aria-expanded]="isAccordionExpanded(config.id)"
                    [attr.aria-controls]="config.id + 'Collapse'"
                    (click)="toggleAccordion(config.id)">
                    {{ config.titleKey | translate }}
                  </button>
                </h2>
                <!-- Accordion Body -->
                <div
                  [id]="config.id + 'Collapse'"
                  class="accordion-collapse collapse"
                  [class.show]="isAccordionExpanded(config.id)"
                  [attr.aria-labelledby]="config.id + 'Heading'"
                  data-bs-parent="#dryerSpecsAccordion">
                  <div class="accordion-body text-start">
                    <ul class="small text-muted font-weight-normal">
                      @for (spec of getSpecificationEntries(config.specifications); track trackBySpecKey($index, spec)) {
                        <li>{{ spec.label | translate }}: {{ spec.value }}</li>
                      }
                    </ul>
                  </div>
                </div>
              </div>
            }
            <!-- Fallback message if no accordions are visible -->
            @if (visibleAccordions.length === 0) {
              <div class="text-center p-4">
                <p class="text-muted">{{ 'NO_DRYER_SPECIFICATIONS_AVAILABLE' | translate }}</p>
              </div>
            }
          </div>
        </div>
      </ng-template>
    }
  </div>
}
